import { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { BsEye, BsPrinter, BsTrash, BsX, BsDownload, BsArrowRepeat } from 'react-icons/bs';
import '../../styles/shared-redesign.css';


// Components
import Table from '../../molecules/tablas/view';
import ButtonAction from '../../atoms/buttonAction/view';
import Chip from '../../atoms/chip/container';
import TooltipC from '../../atoms/tooltip/view';
import Modal from '../../atoms/modal/view';
import LoadingView from '../../pages/loading/loading-view';

// Context & Utils
import { roleContext } from '../../../context/role/context';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import { lang } from '../../../utils/langs';

// Usecases
import { printSell, showInvoicePDF, syncSell } from '../../../usecases/payments-usecase';
import { printReceipt } from '../../../utils/receipt-utils';
import { createPdfBlob, createPdfUrl } from '../../pages/invoice/controller';

function InvoicesTable({
    invoices,
    onReload,
    id = 'unified-invoice-table',
    excludeList = ["cude", "number", "time", "prefix", 'QRStr', 'state', 'uuid', 'uid', 'id', '_original', 'customer_id', 'third_id'],
    isLoading: externalLoading
}) {
    const { profile } = useContext(roleContext);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(externalLoading !== undefined ? externalLoading : false);
    const [pdfUrl, setPdfUrl] = useState(null);

    const handleDownload = () => {
        if (!pdfUrl) return;
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `Factura.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePrintPdf = () => {
        const iframe = document.querySelector('iframe[title="pdf-viewer"]');
        if (iframe) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        }
    };

    useEffect(() => {
        // No fullscreen change listener needed
    }, []);

    const handleClose = useCallback(() => {
        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
    }, [pdfUrl]);

    const closeAll = useCallback(() => {
        dispatchCustomEvent('close-pdf-preview-modal');
        handleClose();
    }, [handleClose]);


    // Limpieza de URLs de PDF
    useEffect(() => {
        window.addEventListener('modal-close', handleClose);
        window.addEventListener('close-modal', handleClose);
        window.addEventListener('close-pdf-preview-modal', handleClose);

        return () => {
            window.removeEventListener('modal-close', handleClose);
            window.removeEventListener('close-modal', handleClose);
            window.removeEventListener('close-pdf-preview-modal', handleClose);
            if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        };
    }, [pdfUrl, handleClose]);

    const isCancelledWithCreditNote = (item) => {
        return !!(
            item?.has_credit_note || 
            item?.has_credit_notes || 
            item?.has_nc || 
            item?.credit_note || 
            (Array.isArray(item?.credit_notes) && item.credit_notes.length > 0) ||
            item?.credit_note_id ||
            item?.nc_id ||
            item?.has_credit ||
            item?.is_cancelled || 
            item?.cancelled ||
            item?.is_credited ||
            item?.credited ||
            item?.state === 'Reversed' || 
            item?.state === 'CreditNoteEmitted'
        );
    };

    const getChipType = (item) => {
        if (isCancelledWithCreditNote(item)) {
            return 'credit-note';
        }
        const state = item?.state || item?.status;
        const types = {
            Emitted: 'success',
            success: 'success',
            pending: 'warning',
            Error: 'error',
            error: 'error',
            Deleted: 'disable',
        };
        return types[state] || 'disable';
    };

    const getStatusLabel = (item) => {
        if (isCancelledWithCreditNote(item)) {
            return 'Anulada con Nota Crédito';
        }
        const state = item?.state || item?.status;
        if (state === 'success' || state === 'Emitted') {
            return 'Emitida';
        }
        if (state === 'pending') {
            return 'Pendiente';
        }
        if (state === 'error' || state === 'Error') {
            return 'Error DIAN';
        }
        if (state === 'Deleted') {
            return 'Eliminada';
        }
        return lang('es', state) || state || 'Desconocido';
    };

    // Detectar si está cargando o sincronizar con externalLoading
    useEffect(() => {
        if (externalLoading !== undefined) {
            setIsLoadingData(externalLoading);
        } else if (Array.isArray(invoices?.data) && invoices.data.length === 0) {
            setIsLoadingData(false);
        } else {
            setIsLoadingData(false);
        }
    }, [externalLoading, invoices?.data]);

    const mappedData = (invoices.data || []).map(item => ({
        date: item.date,
        status: item.error_message ? (
            <TooltipC text={item.error_message}>
                <Chip type={getChipType(item)}>{getStatusLabel(item)}</Chip>
            </TooltipC>
        ) : (
            <Chip type={getChipType(item)}>{getStatusLabel(item)}</Chip>
        ),
        customer: item.customer || '---',
        consecutive: item.consecutive || (item.prefix && item.number ? `${item.prefix}-${item.number}` : item.number || '---'),
        value: item.value,
        _original: item
    }));

    const handleSellPrint = (row) => {
        const data = row._original || row;
        printSell(data.prefix, data.number)
            .then((response) => {
                let res = response;
                res["data"] = res.invoice;
                printReceipt(res, profile);
            })
            .catch(() => {
                dispatchCustomEvent('popup', { message: "No tienes informacion disponible", type: "warning" });
            });
    };

    const handleSyncSell = async (row) => {
        const data = row._original || row;
        const billId = data.bill_id || data.id;
        if (!billId) {
            dispatchCustomEvent('popup', { message: "No se pudo identificar la factura", type: "warning" });
            return;
        }
        setIsLoading(true);
        try {
            const result = await syncSell(billId);
            const status = result?.status;
            if (status === 'success') {
                dispatchCustomEvent('popup', { message: result?.message || "Factura sincronizada con éxito", type: "success" });
                if (onReload) onReload();
            } else if (status === 'pending') {
                dispatchCustomEvent('popup', { message: result?.message || "Aún pendiente en la DIAN. Intente de nuevo en unos minutos.", type: "warning" });
            } else if (status === 'not_found') {
                dispatchCustomEvent('popup', { message: "No existe documento electrónico para esta venta", type: "warning" });
            } else {
                dispatchCustomEvent('popup', { message: result?.message || "Error al sincronizar", type: "error" });
            }
        } catch (error) {
            console.error("Error syncing sell:", error);
            dispatchCustomEvent('popup', { message: "No se pudo sincronizar la factura", type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const handleShowInvoice = async (row) => {
        const data = row._original || row;
        setIsLoading(true);
        try {
            const response = await showInvoicePDF(data.cude || data.cufe);
            // Manejar tanto si response es el objeto de datos o la cadena base64 directamente
            const pdfData = response?.data || response;
            if (!pdfData) throw new Error("No PDF data");

            const blob = createPdfBlob(pdfData);
            const url = createPdfUrl(blob);
            setPdfUrl(url);
            dispatchCustomEvent('modalpdf-preview');
        } catch (error) {
            console.error("Error showing invoice PDF:", error);
            dispatchCustomEvent('popup', { message: "No se pudo cargar el PDF de la factura", type: "warning" });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {isLoading && <LoadingView />}

            <Table
                id={id}
                onReload={onReload}
                headersList={["Fecha", "Estado", "Cliente", "Consecutivo", "Valor"]}
                paginatedData={{ ...invoices, data: mappedData }}
                getFunction={onReload}
                excludeList={excludeList}
                isLoading={isLoadingData}
                allowPageSizeControl={true}
                formats={{
                    value: { type: 'money' },
                    date: { type: 'timestamp' }
                }}
            >
                <ButtonAction title={<BsPrinter />} onClick={handleSellPrint} />
                <ButtonAction title={<BsEye />} onClick={handleShowInvoice} />
                <ButtonAction
                    title={<BsArrowRepeat />}
                    onClick={handleSyncSell}
                    hidden={(row) => {
                        const st = row._original?.state || row.state || row._original?.status || row.status;
                        return st !== 'pending';
                    }}
                />
                <ButtonAction
                    title={<BsTrash />}
                    onClick={(row) => dispatchCustomEvent("notification-confirm-create-nota-credito", row._original || row)}
                    disabled={(row) => {
                        const item = row._original || row;
                        const st = item.state || item.status;
                        
                        // Si ya tiene nota crédito o está anulada/eliminada/revertida, deshabilitar
                        if (isCancelledWithCreditNote(item) || st === 'Deleted' || st === 'Reversed' || st === 'CreditNoteEmitted' || st === 'Cancelled' || st === 'canceled') {
                            return true;
                        }
                        
                        // Solo permitir emitir si el estado es 'Emitted' o 'success'
                        return st !== 'Emitted' && st !== 'success';
                    }}
                />
            </Table>

            <Modal
                id="pdf-preview"
                className="full-viewport-modal"
                onClose={handleClose}
            >
                <div className="pdf-viewer-container">
                    <div className="pdf-viewer-header">
                        <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>Vista Previa de Factura</h3>
                        <div className="pdf-viewer-actions">
                            <button 
                                onClick={handlePrintPdf}
                                className="pdf-viewer-btn pdf-viewer-btn-secondary"
                            >
                                <BsPrinter size={18} />
                                Imprimir
                            </button>

                            <button 
                                onClick={handleDownload}
                                className="pdf-viewer-btn pdf-viewer-btn-secondary"
                            >
                                <BsDownload size={18} />
                                Descargar
                            </button>
                            
                            <button 
                                onClick={closeAll}
                                className="pdf-viewer-btn pdf-viewer-btn-danger"
                            >
                                <BsX size={22} />
                                Cerrar
                            </button>
                        </div>
                    </div>

                    <div className="pdf-viewer-iframe-wrapper">
                        {pdfUrl && (
                            <iframe
                                src={pdfUrl}
                                className="pdf-viewer-iframe"
                                title="pdf-viewer"
                            ></iframe>
                        )}
                    </div>
                </div>
            </Modal>

        </>
    );
}

export default InvoicesTable;
